import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	intro: {
		marginBottom: theme.spacing(2),
		color: theme.palette.text.secondary,
	},
	addRow: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "center",
		marginBottom: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			flexDirection: "column",
			alignItems: "stretch",
		},
	},
	autocomplete: {
		flex: 1,
		minWidth: 240,
	},
	memberList: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
	},
	emptyState: {
		padding: theme.spacing(2),
		color: theme.palette.text.secondary,
	},
	actionButton: {
		borderRadius: 999,
		boxShadow: "none",
		textTransform: "none",
		"&:hover": {
			boxShadow: "none",
		},
	},
}));

const fetchAllUsers = async () => {
	const users = [];
	let pageNumber = 1;
	let hasMore = true;

	while (hasMore) {
		const { data } = await api.get("/users/", {
			params: { pageNumber },
		});
		const batch = Array.isArray(data?.users) ? data.users : [];
		users.push(...batch);
		hasMore = Boolean(data?.hasMore) && batch.length > 0;
		pageNumber += 1;
	}

	return users;
};

const GroupParticipantsModal = ({ open, onClose, ticket }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [members, setMembers] = useState([]);
	const [users, setUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);

	const memberUserIds = useMemo(
		() => new Set(members.map(member => Number(member.userId))),
		[members]
	);

	const availableUsers = useMemo(
		() => users.filter(user => !memberUserIds.has(Number(user.id))),
		[users, memberUserIds]
	);

	const loadData = useCallback(async () => {
		if (!open || !ticket?.id) return;

		setLoading(true);
		try {
			const endpoint = ticket?.isGroup
				? `/tickets/${ticket.id}/group-members`
				: `/tickets/${ticket.id}/participants`;
			const [membersResponse, loadedUsers] = await Promise.all([
				api.get(endpoint),
				fetchAllUsers(),
			]);
			const loadedMembers = ticket?.isGroup
				? membersResponse.data?.members
				: membersResponse.data?.participants;
			setMembers(Array.isArray(loadedMembers) ? loadedMembers : []);
			setUsers(loadedUsers);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, [open, ticket?.id]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const handleAddMember = async () => {
		if (!selectedUser?.id || !ticket?.id) return;

		setLoading(true);
		try {
			const endpoint = ticket?.isGroup
				? `/tickets/${ticket.id}/group-members`
				: `/tickets/${ticket.id}/participants`;
			const { data } = await api.post(endpoint, {
				userId: selectedUser.id,
				role: "member",
			});
			setMembers(prevState => [...prevState.filter(member => Number(member.userId) !== Number(data.userId)), data]);
			setSelectedUser(null);
			toast.success(i18n.t("groupParticipantsModal.toasts.added"));
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveMember = async userId => {
		if (!ticket?.id) return;

		setLoading(true);
		try {
			const endpoint = ticket?.isGroup
				? `/tickets/${ticket.id}/group-members/${userId}`
				: `/tickets/${ticket.id}/participants/${userId}`;
			await api.delete(endpoint);
			setMembers(prevState => prevState.filter(member => Number(member.userId) !== Number(userId)));
			toast.success(i18n.t("groupParticipantsModal.toasts.removed"));
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{i18n.t(ticket?.isGroup ? "groupParticipantsModal.title" : "groupParticipantsModal.ticketTitle")}
			</DialogTitle>
			<DialogContent dividers>
				<Typography className={classes.intro}>
					{i18n.t(ticket?.isGroup ? "groupParticipantsModal.description" : "groupParticipantsModal.ticketDescription")}
				</Typography>
				<div className={classes.addRow}>
					<Autocomplete
						className={classes.autocomplete}
						options={availableUsers}
						loading={loading}
						value={selectedUser}
						onChange={(event, value) => setSelectedUser(value)}
						getOptionLabel={option => option?.name || option?.email || ""}
						getOptionSelected={(option, value) => Number(option.id) === Number(value.id)}
						renderInput={params => (
							<TextField
								{...params}
								variant="outlined"
								label={i18n.t("groupParticipantsModal.userLabel")}
								placeholder={i18n.t("groupParticipantsModal.userPlaceholder")}
							/>
						)}
					/>
					<Button
						variant="contained"
						color="primary"
						disableElevation
						className={classes.actionButton}
						disabled={loading || !selectedUser}
						onClick={handleAddMember}
					>
						{i18n.t("groupParticipantsModal.buttons.add")}
					</Button>
				</div>
				<List className={classes.memberList} dense>
					{members.length === 0 && (
						<Typography className={classes.emptyState}>
							{i18n.t("groupParticipantsModal.empty")}
						</Typography>
					)}
					{members.map(member => (
						<ListItem key={member.id || member.userId} divider>
							<ListItemText
								primary={member.user?.name || member.user?.email || `#${member.userId}`}
								secondary={member.user?.email}
							/>
							<Chip size="small" label={i18n.t(`groupParticipantsModal.roles.${member.role || "member"}`)} />
							<ListItemSecondaryAction>
								<IconButton
									edge="end"
									aria-label={i18n.t("groupParticipantsModal.buttons.remove")}
									onClick={() => handleRemoveMember(member.userId)}
									disabled={loading}
								>
									<DeleteIcon />
								</IconButton>
							</ListItemSecondaryAction>
						</ListItem>
					))}
				</List>
			</DialogContent>
			<DialogActions>
				<Button
					className={classes.actionButton}
					variant="contained"
					disableElevation
					onClick={onClose}
				>
					{i18n.t("groupParticipantsModal.buttons.close")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default GroupParticipantsModal;
